import React, {useState} from 'react'
import {sendCoApplicantInvite} from '../../api'
import {IWizardCoApplicant} from '../../models/application-form'
import {isValidAustralianMobile, normalizeAustralianMobile} from './applicationPersonUtils'
import {ApplicationPersonModal, ApplicationPersonModalErrors} from './ApplicationPersonModal'
import {useRetryCountdown} from './useRetryCountdown'

type ExistingCoApplicant = Pick<IWizardCoApplicant, 'mobileNumber' | 'status'>

export type CoApplicantInviteSuccess = {
  invite: IWizardCoApplicant
  message?: string
  retryAfterSeconds: number
}

type CoApplicantInviteModalProps = {
  isOpen: boolean
  sessionId?: string
  ownerMobileNumber?: string
  existingCoApplicants?: ExistingCoApplicant[]
  onClose: () => void
  onInvited: (result: CoApplicantInviteSuccess) => void | Promise<void>
}

type InviteDraft = {
  fullName: string
  mobileNumber: string
}

const createEmptyInvite = (): InviteDraft => ({
  fullName: '',
  mobileNumber: '',
})

const CoApplicantInviteModal: React.FC<CoApplicantInviteModalProps> = ({
  isOpen,
  sessionId,
  ownerMobileNumber,
  existingCoApplicants = [],
  onClose,
  onInvited,
}) => {
  const [draft, setDraft] = useState<InviteDraft>(createEmptyInvite)
  const [errors, setErrors] = useState<ApplicationPersonModalErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const retry = useRetryCountdown()

  const closeModal = () => {
    setDraft(createEmptyInvite())
    setErrors({})
    setSubmitError(null)
    onClose()
  }

  const validate = (): ApplicationPersonModalErrors => {
    const nextErrors: ApplicationPersonModalErrors = {}
    const normalizedMobile = normalizeAustralianMobile(draft.mobileNumber)

    if (!draft.fullName.trim()) {
      nextErrors.fullName = 'Enter their full name.'
    }

    if (!normalizedMobile || !isValidAustralianMobile(normalizedMobile)) {
      nextErrors.mobileNumber = 'Enter a valid Australian mobile number.'
    } else if (normalizedMobile === normalizeAustralianMobile(ownerMobileNumber ?? '')) {
      nextErrors.mobileNumber = "That's your own number - add someone else"
    } else if (
      existingCoApplicants.some(
        (applicant) =>
          applicant.status !== 'revoked' &&
          normalizeAustralianMobile(applicant.mobileNumber ?? '') === normalizedMobile
      )
    ) {
      nextErrors.mobileNumber = 'The number is already in the application.'
    }

    return nextErrors
  }

  const sendInvite = async () => {
    if (isSending || retry.seconds > 0) return
    const nextErrors = validate()
    setErrors(nextErrors)
    setSubmitError(null)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    const normalizedSessionId = sessionId?.trim()
    if (!normalizedSessionId) {
      setSubmitError('Session expired. Please verify your mobile again.')
      return
    }

    setIsSending(true)

    try {
      const response = await sendCoApplicantInvite({
        sessionId: normalizedSessionId,
        fullName: draft.fullName.trim(),
        mobileNumber: normalizeAustralianMobile(draft.mobileNumber),
        email: '',
        role: 'co-applicant',
      })

      if (!response.success || !response.invite) {
        retry.start(response.retryAfterSeconds)
        setSubmitError(response.message ?? 'Unable to send invite right now.')
        return
      }

      await onInvited({
        invite: {
          inviteId: response.invite.inviteId,
          fullName: response.invite.fullName,
          mobileNumber: normalizeAustralianMobile(response.invite.mobileNumber),
          email: response.invite.email,
          role: response.invite.role,
          status: response.invite.status,
          isMobileVerified: response.invite.isMobileVerified,
          invitedAtUtc: response.invite.invitedAtUtc,
          inviteExpiresAtUtc: response.invite.inviteExpiresAtUtc,
          verifiedAtUtc: response.invite.verifiedAtUtc,
          revokedAtUtc: response.invite.revokedAtUtc,
          declinedAtUtc: response.invite.declinedAtUtc,
          declineReason: response.invite.declineReason
        },
        message: response.message,
        retryAfterSeconds: response.retryAfterSeconds ?? 30,
      })

      closeModal()
    } catch {
      setSubmitError('Unable to send invite right now.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <ApplicationPersonModal
      title='Add a person'
      submitLabel={retry.seconds > 0 ? `Try again in ${retry.seconds}s` : 'Send Invite'}
      isOpen={isOpen}
      isSubmitting={isSending}
      submitDisabled={retry.seconds > 0}
      onClose={closeModal}
      onSubmit={sendInvite}
      fullName={draft.fullName}
      mobileNumber={draft.mobileNumber}
      onFullNameChange={(fullName) => setDraft((current) => ({...current, fullName}))}
      onMobileNumberChange={(mobileNumber) => setDraft((current) => ({...current, mobileNumber}))}
      errors={errors}
      submitError={submitError}
      closeAriaLabel='Close invite dialog'
    />
  )
}

export default CoApplicantInviteModal
